import { FormLite } from '@tableau/auto-form';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { useFormikContext } from '~src/settings/FormikWrapperForStubbing';
import { useCanAccessSetting } from '~src/settings/hooks/useCanAccessSetting';
import { MIGRATED_FORMIK_PREFIX, useMigratedFormikPrefix } from '~src/settings/hooks/useMigratedFormikPrefix';
import { useSiteSettingsMetadata } from '~src/settings/hooks/useSiteSettingsMetadata';
import { useSiteSettingsSchema } from '~src/settings/hooks/useSiteSettingsSchema';
import { GetSiteSettingsService } from '~src/settings/site-settings/restApi/requests/GetSiteSettings';
import { ISiteSettingMetadata } from '~src/settings/site-settings/restApi/types/ISiteSettingsMetadata';

export type FormikFormLiteProps = {
  sectionName: string;
  formikPrefix: string;
};

const SiteSettingsAutoForm = ({ sectionName, formikPrefix }: FormikFormLiteProps) => {
  const { schema, isLoading, error } = useSiteSettingsSchema(sectionName);
  const { sectionMetadata } = useSiteSettingsMetadata(sectionName);
  const { setFieldValue, getFieldProps, values } = useFormikContext<any>();

  // Check if user has capability to access this section
  const canAccess = useCanAccessSetting(sectionName);

  // Decide whether this section should write to the legacy prefix (e.g. settings)
  // or to the V2 prefix (e.g. settings.siteSettingsV2) based on metadata + feature flags
  const migratedPrefix = useMigratedFormikPrefix(sectionName, formikPrefix);

  const settingKeys = React.useMemo(
    () => sectionMetadata?.siteSettingMetadata.map(m => m.name) ?? [],
    [sectionMetadata]
  );

  // Fetch current server values for display. Used as a fallback when Formik has no
  // value yet — i.e. for Admin Profile users whose siteSettingsV2 starts as {}.
  // Site/Server Admins already have siteSettingsV2 populated from the legacy path,
  // so their Formik values always take priority and this result is unused for them.
  // Values are never written into Formik; changedDataHandler fires only on user
  // interaction, so siteSettingsV2 on submit contains only user-changed keys.
  const { data: serverPairs } = useQuery({
    queryKey: ['siteSettingsValues', sectionName],
    queryFn: async () => {
      const response = await GetSiteSettingsService.getSiteSettings(settingKeys);
      return response.siteSetting ?? [];
    },
    enabled: migratedPrefix === MIGRATED_FORMIK_PREFIX && settingKeys.length > 0,
    staleTime: Infinity,
  });

  const serverValues = React.useMemo(() => {
    if (!serverPairs || !sectionMetadata) return {};
    const metaByKey = new Map<string, ISiteSettingMetadata>(
      sectionMetadata.siteSettingMetadata.map(m => [m.name, m])
    );
    const result: Record<string, unknown> = {};
    serverPairs.forEach(({ key, value }) => {
      const meta = metaByKey.get(key);
      result[key] = meta ? coerceMetadataValue(meta, value) : value;
    });
    return result;
  }, [serverPairs, sectionMetadata]);

  const changedDataHandler = React.useCallback((changedData: any) => {
    for (const [key, value] of Object.entries(changedData)) {
      setFieldValue(migratedPrefix + '.' + key, value, false);
    }
  }, [setFieldValue, migratedPrefix]);

  // Formik value takes priority (user has changed it). Fall back to current server
  // value so the form shows the actual setting state rather than the schema default.
  const data = React.useMemo(() => {
    if (!schema?.properties) return {};
    return schema.properties.reduce<any>((acc, prop) => {
      const formikValue = getFieldProps(migratedPrefix + '.' + prop.name).value;
      acc[prop.name] = formikValue !== undefined ? formikValue : serverValues[prop.name];
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, migratedPrefix, values, serverValues]);

  // Don't render if user doesn't have capability to access this section
  if (!canAccess) {
    return null;
  }

  if (isLoading || error || !schema || !schema.properties) {
    return null;
  }

  return (
    <FormLite
      schema={schema}
      data={data}
      dataChanged={changedDataHandler}
    />
  );
};

export const MetadataDrivenSiteSettings = (props: FormikFormLiteProps) => {
  return <SiteSettingsAutoForm {...props} />;
};

function coerceMetadataValue(meta: ISiteSettingMetadata, value: string): unknown {
  switch (meta.type) {
    case 'Boolean':
      return value === 'true';
    case 'Integer': {
      const n = Number(value);
      return Number.isFinite(n) ? n : value;
    }
    default:
      return value;
  }
}
